import { IDocument } from '@shared';
import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LoggedInUser } from '../models/logged-in-user';
import { MsalService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Store } from '@ngrx/store';
import { AuthActions } from '../store';
import { RootState } from '../../root-store';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ICurrentClient, SubscriptionOptions } from '../../models';
import { exhaustMap, from, Observable, Subject } from 'rxjs';
import { IBillingInfo, IBillingPlanDTO, IBillingSubscription } from 'src/app/billing/models/billing.models';
import { AppInsightsService } from '../../analytics/app-insights.service';

@Injectable()
export class AuthService {
  private logoutSubject = new Subject<void>();
  constructor(
    private http: HttpClient,
    private msal: MsalService,
    private store: Store<RootState>,
    private appInsights: AppInsightsService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
  ) { }

  login() {
    return this.msal.loginPopup({...this.msalGuardConfig.authRequest} as PopupRequest)
      .pipe(
        exhaustMap(response => {
          this.msal.instance.setActiveAccount(response.account);
          if (response.account) this.appInsights.setLogin(response.account.username);
          return this.getProfile();
        }));
  }

  loginRedirect(){
    console.log('loginRedirect');
    this.msal.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
  }

  handleRedirectObservable(): Observable<AuthenticationResult> {
    return from(this.msal.instance.handleRedirectPromise()).pipe(
      takeUntil(this.logoutSubject),
      filter((result): result is AuthenticationResult => !!result),
      map((result) => {
        if (result && result.account) {
          // Set the active account
          this.getProfile();
          this.msal.instance.setActiveAccount(result.account);
          return result;
        }
        throw new Error('Login failed');
      }),
      catchError(error => {
        throw new Error(error);
      })
    );
  }

  isLoggedIn(): boolean {
    return this.msal.instance.getAllAccounts().length > 0;
  }

  getUser(): string | null {
    const account = this.msal.instance.getActiveAccount();
    return account ? account.username : null;
  }

  logoutIdleUser() {
    return this.msal.logout({
      onRedirectNavigate: () =>  false
      });
  }

  getDocumentsToSign() {
      return this.http.get<IDocument[]>('acknowledge/userDocuments');
  }

  signDocuments(userDocumentIds: number[]) {
    return this.http.post<boolean>('acknowledge/userDocuments', userDocumentIds);
  }

  getBillingPlan() {
    return this.http.get<IBillingPlanDTO>('billing/plan');
  }

  createBillingInfo(data: IBillingInfo) {
    return this.http.put('billing/billinginfo', data);
  }

  createSubscription(data: IBillingSubscription) {
    return this.http.post('billing/subscription', data);
  }

  getProfile() {
    return this.http.get<LoggedInUser>(`me`).pipe(
      tap(user => this.store.dispatch(AuthActions.loadUserProfileSuccess(user)))
    );
  }

  setCurrentClient(clientId: string) {
    // because client id is not set we have to manually add client header
    const headers = new HttpHeaders().set('x-c', clientId);
    return this.http.get<ICurrentClient>(`clients/${clientId}`, { headers }).pipe(
      tap(client => this.store.dispatch(AuthActions.currentClientLoadSuccess({ client })))
    );
  }

  setTheme(darkTheme:boolean){
    this.http.post('me/theme', {darkTheme}).subscribe();
  }

  logout() {
    this.logoutSubject.next();
    this.logoutSubject.complete();
    return this.msal.logoutRedirect();
  }

  getClientRedirectUrl(subscription: number): string {
    // const isSupplier = !!(subscription & SubscriptionOptions.SupplierMarketplace);
    // return isSupplier
    //   ? 'supplier-home'
    //   : 'home'
    return 'home';
  }
}
